import { Link } from 'react-router-dom';
import { ClipboardCheck, Users2, CalendarClock, ShieldCheck, MapPin, Quote, Star, User, Building2, ArrowRight } from 'lucide-react';
import { useFetch } from '../../hooks/useFetch';
import { temoignagesApi, settingsApi } from '../../api/contentApi';
import { articlesApi } from '../../api/articlesApi';
import { servicesApi } from '../../api/servicesApi';
import { Button } from '../../components/ui/Button';
import { ArticleCard } from '../../components/ui/ArticleCard';
import { IconBadge } from '../../components/ui/IconBadge';
import { Reveal } from '../../components/ui/Reveal';
import { Hero } from '../../components/ui/Hero';
import { TrustBar } from '../../components/ui/TrustBar';
import { StatCounter } from '../../components/ui/StatCounter';
import { getServiceIcon } from '../../utils/serviceIcons';
import { Seo } from '../../components/ui/Seo';
import './Home.css';

const ETAPES = [
  {
    icon: ClipboardCheck,
    titre: 'Vous décrivez votre besoin',
    description: 'En 2 minutes via notre formulaire de devis, en ligne ou par téléphone.',
  },
  {
    icon: Users2,
    titre: 'Nous vous proposons un intervenant',
    description: 'Un professionnel qualifié, sélectionné et formé, adapté à votre demande.',
  },
  {
    icon: CalendarClock,
    titre: 'L’intervention a lieu',
    description: 'Au jour et à l’heure convenus, avec un suivi de la prestation.',
  },
  {
    icon: ShieldCheck,
    titre: 'Votre satisfaction est suivie',
    description: 'Un interlocuteur unique reste disponible pour tout ajustement.',
  },
];

const CHIFFRES = [
  { valeur: '10 ans', label: "d'expérience" },
  { valeur: '500+', label: 'clients accompagnés' },
  { valeur: '98%', label: 'de clients satisfaits' },
  { valeur: '7j/7', label: 'disponibilité' },
];

export function Home() {
  const { data: temoignages } = useFetch(() => temoignagesApi.list(), []);
  const { data: articles } = useFetch(() => articlesApi.list(3, 0), []);
  const { data: categoriesParticulier } = useFetch(() => servicesApi.categories('particulier'), []);
  const { data: categoriesPro } = useFetch(() => servicesApi.categories('professionnel'), []);
  const { data: settings } = useFetch(() => settingsApi.public(), []);

  const apercuParticulier = categoriesParticulier?.slice(0, 4) ?? [];
  const apercuPro = categoriesPro?.slice(0, 4) ?? [];
  const heroImage = settings?.['hero.image_url'];
  const heroEyebrow = settings?.['hero.eyebrow'] || 'Services à la Personne & Solutions Professionnelles';
  const heroTitre = settings?.['hero.titre'] || 'Un seul interlocuteur, deux expertises';
  const heroSousTitre =
    settings?.['hero.sous_titre'] ||
    "Chez vous ou dans vos locaux, nous intervenons avec la même exigence : ménage, jardinage et bricolage pour les particuliers ; nettoyage, entretien et maintenance pour les professionnels.";
  const zonesTexte =
    settings?.['zones.description'] || 'Nous intervenons en Île-de-France et dans les grandes métropoles limitrophes.';
  const zones = zonesTexte
    .split('\n')
    .map((z) => z.trim())
    .filter(Boolean);

  return (
    <>
      <Seo
        title="Ménage, jardinage & entretien à Cergy-Pontoise"
        description="AXEO Services Cergy : ménage, jardinage, bricolage et aide à domicile pour les particuliers ; nettoyage et entretien pour les professionnels. Intervention à Cergy, Pontoise, Éragny et dans toute la Communauté d'Agglomération de Cergy-Pontoise."
        path="/"
      />

      {/* --- Hero + séparation des univers --- */}
      <Hero
        eyebrow={heroEyebrow}
        title={heroTitre}
        subtitle={heroSousTitre}
        imageUrl={heroImage}
        tone="neutral"
      >
        <div className="hero__univers">
          <Link to="/particuliers" className="hero__univers-card hero__univers-card--primary">
            <span className="hero__univers-icon">
              <User size={20} strokeWidth={2.2} />
            </span>
            <span className="hero__univers-text">
              <span className="hero__univers-label">Je suis un particulier</span>
              <span className="hero__univers-desc">Ménage, repassage, jardinage, petit bricolage</span>
            </span>
            <ArrowRight className="hero__univers-arrow" size={20} strokeWidth={2.2} />
          </Link>
          <Link to="/professionnels" className="hero__univers-card hero__univers-card--secondary">
            <span className="hero__univers-icon">
              <Building2 size={20} strokeWidth={2.2} />
            </span>
            <span className="hero__univers-text">
              <span className="hero__univers-label">Je suis un professionnel</span>
              <span className="hero__univers-desc">Nettoyage, entretien, maintenance multiservices</span>
            </span>
            <ArrowRight className="hero__univers-arrow" size={20} strokeWidth={2.2} />
          </Link>
        </div>
      </Hero>

      <TrustBar />

      {/* --- Nos services --- */}
      {(apercuParticulier.length > 0 || apercuPro.length > 0) && (
        <section className="section">
          <div className="container">
            <Reveal as="div" className="section__header">
              <h2>Nos services</h2>
              <p className="section__intro">
                Un large éventail de prestations, pour les particuliers comme pour les professionnels.
              </p>
            </Reveal>

            <div className="services-split">
              {apercuParticulier.length > 0 && (
                <Reveal as="div" className="services-split__col services-split__col--primary">
                  <div className="services-split__head">
                    <span className="services-split__icon">
                      <User size={17} strokeWidth={2.2} />
                    </span>
                    <div>
                      <h3>Services à la personne</h3>
                      <p>Chez vous, au quotidien</p>
                    </div>
                  </div>
                  <ul className="services-split__list">
                    {apercuParticulier.map((cat) => {
                      const Icon = getServiceIcon(cat.nom);
                      return (
                        <li key={cat.id}>
                          <Link to="/particuliers" className="services-split__row">
                            <Icon size={16} strokeWidth={2} className="services-split__row-icon" />
                            <span>{cat.nom}</span>
                            <ArrowRight size={15} className="services-split__row-arrow" />
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                  <Link to="/particuliers" className="services-split__more">
                    Voir tous les services particuliers <ArrowRight size={16} />
                  </Link>
                </Reveal>
              )}

              {apercuPro.length > 0 && (
                <Reveal as="div" className="services-split__col services-split__col--secondary" delay={80}>
                  <div className="services-split__head">
                    <span className="services-split__icon">
                      <Building2 size={17} strokeWidth={2.2} />
                    </span>
                    <div>
                      <h3>Solutions professionnelles</h3>
                      <p>Pour vos locaux et équipes</p>
                    </div>
                  </div>
                  <ul className="services-split__list">
                    {apercuPro.map((cat) => {
                      const Icon = getServiceIcon(cat.nom);
                      return (
                        <li key={cat.id}>
                          <Link to="/professionnels" className="services-split__row">
                            <Icon size={16} strokeWidth={2} className="services-split__row-icon" />
                            <span>{cat.nom}</span>
                            <ArrowRight size={15} className="services-split__row-arrow" />
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                  <Link to="/professionnels" className="services-split__more">
                    Voir toutes les solutions pro <ArrowRight size={16} />
                  </Link>
                </Reveal>
              )}
            </div>
          </div>
        </section>
      )}

      {/* --- Comment ça marche --- */}
      <section className="section section--accent">
        <div className="container">
          <Reveal as="div" className="section__header">
            <h2>Comment ça marche</h2>
            <p className="section__intro">De la demande à l'intervention, un parcours simple et suivi.</p>
          </Reveal>
          <div className="etapes">
            {ETAPES.map((etape, i) => (
              <Reveal as="div" className="etape" key={etape.titre} delay={i * 80}>
                <IconBadge icon={etape.icon} tone="primary" />
                <h3>{etape.titre}</h3>
                <p>{etape.description}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* --- Chiffres --- */}
      <section className="section">
        <div className="container">
          <div className="section__grid section__grid--4">
            {CHIFFRES.map((c) => (
              <StatCounter key={c.label} {...c} />
            ))}
          </div>
        </div>
      </section>

      {/* --- Avis clients --- */}
      {temoignages?.length > 0 && (
        <section className="section section--accent">
          <div className="container">
            <Reveal as="div" className="section__header">
              <h2>Ils nous font confiance</h2>
            </Reveal>

            {temoignages.length >= 4 ? (
              <div className="marquee" style={{ '--marquee-duration': `${temoignages.length * 14}s` }}>
                <div className="marquee__track">
                  {[...temoignages, ...temoignages].map((t, i) => (
                    <TemoignageCard key={`${t.id}-${i}`} temoignage={t} />
                  ))}
                </div>
              </div>
            ) : (
              <div className="temoignages-grid">
                {temoignages.map((t) => (
                  <TemoignageCard key={t.id} temoignage={t} />
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* --- Zone d'intervention --- */}
      <section className="section">
        <div className="container">
          <Reveal as="div" className="zone-section">
            <div className="zone-section__head">
              <span className="zone-section__icon">
                <MapPin size={17} strokeWidth={2.2} />
              </span>
              <div>
                <h2>Zone d'intervention</h2>
                <p>Nous intervenons dans les communes suivantes</p>
              </div>
            </div>
            <ul className="zone-section__tags">
              {zones.map((zone) => (
                <li key={zone} className="zone-section__tag">
                  {zone}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* --- Actualités --- */}
      {articles?.length > 0 && (
        <section className="section section--accent">
          <div className="container">
            <Reveal as="div" className="section__header">
              <h2>Dernières actualités</h2>
            </Reveal>
            <div className="section__grid section__grid--3">
              {articles.map((article, i) => (
                <Reveal key={article.id} delay={i * 60}>
                  <ArticleCard article={article} />
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* --- CTA final --- */}
      <section className="section section--cta">
        <div className="container">
          <h2>Un projet ? Parlons-en.</h2>
          <Link to="/devis">
            <Button className="btn--pulse">Demander un devis gratuit</Button>
          </Link>
        </div>
      </section>
    </>
  );
}

function TemoignageCard({ temoignage: t }) {
  return (
    <blockquote className="temoignage">
      <Quote className="temoignage__quote-icon" strokeWidth={2} aria-hidden="true" />
      {t.note && (
        <div className="temoignage__stars" aria-label={`Note : ${t.note} sur 5`}>
          {Array.from({ length: 5 }, (_, i) => (
            <Star key={i} size={14} className={i < t.note ? 'temoignage__star--filled' : 'temoignage__star'} />
          ))}
        </div>
      )}
      <p>{t.contenu}</p>
      <footer>
        — {t.nom}
        {t.entreprise ? `, ${t.entreprise}` : ''}
      </footer>
    </blockquote>
  );
}
